import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Card, Button, Form, Spinner, Alert, Pagination, Placeholder, Row, Col } from 'react-bootstrap';
import { getTickers, getTickerNotes, addTickerNote, editTickerNote, deleteTickerNote } from '../services/api';
import { formatDateTime } from '../utils/dateUtils';

function NotesDetails() {
  const { symbol } = useParams();
  const [ticker, setTicker] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Separate from `error`: `error` gates the whole page (replaced entirely
  // by a bare alert while it's set) and is only ever set by the initial
  // load. A transient failure on an action (add/edit/delete) sets this
  // instead, so it surfaces as a dismissible inline alert without tearing
  // down the notes grid, pagination, and add-note form underneath it.
  const [actionError, setActionError] = useState('');
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('notesDetailsPageSize');
    const n = saved ? parseInt(saved) : 10;
    return [3, 5, 10].includes(n) ? n : 10;
  });
  const [numColumns, setNumColumns] = useState(() => {
    const saved = localStorage.getItem('notesDetailsColumns');
    const n = saved ? parseInt(saved) : 3;
    return [1,2,3,4].includes(n) ? n : 3;
  });

  // formatLocalDateTime function is now imported from dateUtils

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const tickersResp = await getTickers();
        const list = tickersResp.data || [];
        const found = list.find(t => t.symbol?.toUpperCase() === decodeURIComponent(symbol).toUpperCase());
        if (!found) {
          setError('Ticker not found');
          setLoading(false);
          return;
        }
        setTicker(found);
        const notesResp = await getTickerNotes(found.ticker_id);
        const listNotes = (notesResp.data || []).sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
        setNotes(listNotes);
      } catch (e) {
        setError('Failed to load notes');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [symbol]);

  const handleAdd = async () => {
    if (!ticker || !newNote.trim()) return;
    try {
      setSaving(true);
      setActionError('');
      await addTickerNote(ticker.ticker_id, newNote.trim());
      const notesResp = await getTickerNotes(ticker.ticker_id);
      // Re-apply the same newest-first sort load() uses -- without this,
      // display order after adding depended on whatever raw order the
      // backend happened to return notes in.
      const listNotes = (notesResp.data || []).sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      setNotes(listNotes);
      setNewNote('');
    } catch (e) {
      setActionError('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (noteId, value) => {
    try {
      setActionError('');
      await editTickerNote(noteId, value);
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, note: value } : n));
      setEditingId(null);
      setEditingValue('');
    } catch (e) {
      setActionError('Failed to edit note');
    }
  };

  const handleDelete = async (noteId) => {
    try {
      setActionError('');
      await deleteTickerNote(noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (e) {
      setActionError('Failed to delete note');
    }
  };

  const totalCount = notes.length;
  // Calculate how many notes fit in one row based on columns
  const notesPerRow = numColumns;
  // Calculate total rows needed
  const totalRows = Math.ceil(totalCount / notesPerRow);
  // Calculate total pages based on rows, not notes
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const currentPageSafe = Math.min(currentPage, totalPages);
  // Calculate start and end rows
  const startRow = (currentPageSafe - 1) * pageSize;
  const endRow = Math.min(startRow + pageSize, totalRows);
  // Calculate start and end note indices
  const startIndex = startRow * notesPerRow;
  const endIndex = Math.min(startIndex + (pageSize * notesPerRow), totalCount);
  const visibleNotes = notes.slice(startIndex, endIndex);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (e) => { const v = parseInt(e.target.value); setPageSize(v); localStorage.setItem('notesDetailsPageSize', String(v)); setCurrentPage(1); };
  const handleColumnsChange = (e) => { const v = parseInt(e.target.value); setNumColumns(v); localStorage.setItem('notesDetailsColumns', String(v)); };

  const renderPagination = () => {
    const items = [];
    const maxItems = 7;
    items.push(
      <Pagination.Item key={1} active={currentPageSafe === 1} onClick={() => handlePageChange(1)}>1</Pagination.Item>
    );
    let startPage = Math.max(2, currentPageSafe - 2);
    let endPage = Math.min(totalPages - 1, currentPageSafe + 2);
    if (currentPageSafe <= 3) endPage = Math.min(totalPages - 1, maxItems - 2);
    else if (currentPageSafe >= totalPages - 2) startPage = Math.max(2, totalPages - maxItems + 2);
    if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={currentPageSafe === i} onClick={() => handlePageChange(i)}>{i}</Pagination.Item>
      );
    }
    if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    if (totalPages > 1) {
      items.push(
        <Pagination.Item key={totalPages} active={currentPageSafe === totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>
      );
    }
    return (
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
        <div className="d-flex align-items-center">
          <span className="me-2">Rows per page:</span>
          <Form.Select size="sm" value={pageSize} onChange={handlePageSizeChange} style={{ width: '80px' }}>
            <option value="3">3</option>
            <option value="5">5</option>
            <option value="10">10</option>
          </Form.Select>
          <span className="ms-3 me-2">Columns:</span>
          <Form.Select size="sm" value={numColumns} onChange={handleColumnsChange} style={{ width: '80px' }}>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
          </Form.Select>
          <span className="ms-3">
            {totalCount > 0 ? (
              <>Showing rows {startRow + 1} to {endRow} of {totalRows} ({startIndex + 1} to {endIndex} of {totalCount} notes)</>
            ) : 'No notes found'}
          </span>
        </div>
        <Pagination size="sm">
          <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPageSafe === 1} />
          <Pagination.Prev onClick={() => handlePageChange(Math.max(1, currentPageSafe - 1))} disabled={currentPageSafe === 1} />
          {items}
          <Pagination.Next onClick={() => handlePageChange(Math.min(totalPages, currentPageSafe + 1))} disabled={currentPageSafe === totalPages} />
          <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPageSafe === totalPages} />
        </Pagination>
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="py-4 text-center"><Spinner animation="border" size="sm" /></Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4"><Alert variant="danger">{error}</Alert></Container>
    );
  }

  return (
    <Container className="py-4">
      {actionError && (
        <Alert variant="danger" dismissible onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}
      <Card className="mb-3">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div>
            <Card.Title className="mb-0">{ticker.symbol} — {ticker.name}</Card.Title>
          </div>
        </Card.Body>
      </Card>

      <div>
        {loading ? (
          <Row className="g-3">
            {Array.from({ length: Math.min(pageSize * numColumns, pageSize * numColumns) }).map((_, idx) => (
              <Col key={idx} md={Math.max(1, Math.min(12, Math.floor(12 / numColumns)))}>
                <div className="border rounded p-2">
                  <Placeholder as="div" animation="glow"><Placeholder xs={8} /></Placeholder>
                  <Placeholder as="div" animation="glow"><Placeholder xs={6} /></Placeholder>
                </div>
              </Col>
            ))}
          </Row>
        ) : (
          visibleNotes.length === 0 ? (
            <div className="text-muted">No notes yet.</div>
          ) : (
            <Row className="g-3">
              {visibleNotes.map(n => (
                <Col key={n.id} md={Math.max(1, Math.min(12, Math.floor(12 / numColumns)))}>
                  <div className="border rounded p-2 h-100 d-flex flex-column" style={{ whiteSpace: 'pre-wrap' }}>
                                        <div className="d-flex justify-content-between align-items-start">
                        <div className="text-muted small">{formatDateTime(n.updated_at || n.created_at)}</div>
                      <div className="ms-2 d-flex gap-1">
                        {editingId === n.id ? null : (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="py-0 px-1"
                            title="Edit"
                            aria-label="Edit"
                            onClick={() => { setEditingId(n.id); setEditingValue(n.note); }}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                        )}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="py-0 px-1"
                          title="Delete"
                          aria-label="Delete"
                          onClick={() => handleDelete(n.id)}
                        >
                          <i className="bi bi-trash" style={{ color: '#dc3545' }}></i>
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 flex-grow-1">
                      {editingId === n.id ? (
                        <>
                          <Form.Control
                            as="textarea"
                            rows={5}
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                          />
                          <div className="mt-2 d-flex gap-2">
                            <Button variant="primary" size="sm" onClick={() => handleEdit(n.id, editingValue.trim())} disabled={!editingValue.trim()}>Save</Button>
                            <Button variant="secondary" size="sm" onClick={() => { setEditingId(null); setEditingValue(''); }}>Cancel</Button>
                          </div>
                        </>
                      ) : (
                        <div>{n.note}</div>
                      )}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )
        )}
      </div>

      {renderPagination()}

      <div className="mt-3">
        <Form onSubmit={(e) => { e.preventDefault(); handleAdd(); }}>
          <Form.Control as="textarea" rows={3} value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Write a new note..." />
          <div className="mt-2 d-flex justify-content-end">
            <Button variant="primary" onClick={handleAdd} disabled={saving || !newNote.trim()}>
              {saving ? 'Saving...' : 'Add Note'}
            </Button>
          </div>
        </Form>
      </div>
    </Container>
  );
}

export default NotesDetails;


